/**
 * Distributes an integer total across N values using the largest remainder method,
 * ensuring the result always sums exactly to `total`.
 *
 * @param exactValues - Raw (fractional) values whose sum equals `total`
 * @returns Integer values in the original order that sum exactly to `total`
 */
export function largestRemainderIntegers(exactValues: number[]): number[] {
  const floored = exactValues.map(Math.floor);
  let remaining = Math.round(exactValues.reduce((s, v) => s + v, 0)) - floored.reduce((s, v) => s + v, 0);

  const order = exactValues.map((v, i) => ({ i, frac: v - Math.floor(v) })).sort((a, b) => b.frac - a.frac);

  const result = [...floored];
  for (const { i } of order) {
    if (remaining <= 0) break;
    result[i]++;
    remaining--;
  }
  return result;
}

export interface PlannedHoursValidation {
  level: 'warning' | 'error';
  message: string;
}

export function validatePlannedHours(
  rowsCount: number,
  rowsTotalHours: number,
  plannedHours: number,
): PlannedHoursValidation | null {
  if (rowsCount === 0) return null;
  if (plannedHours === 0) {
    return { level: 'warning', message: 'Planned Hours must be greater than 0 when activities are defined.' };
  }
  if (plannedHours < rowsCount) {
    return {
      level: 'warning',
      message: `Planned Hours must be at least ${rowsCount} (1 per activity). Currently ${
        rowsCount - plannedHours
      } hour(s) short.`,
    };
  }
  const diff = rowsTotalHours - plannedHours;
  if (diff !== 0) {
    const detail = diff < 0 ? `${Math.abs(diff)}h still to assign.` : `${diff}h over the limit.`;
    return {
      level: 'error',
      message: `Sum of activity hours (${rowsTotalHours}h) must equal Planned Hours (${plannedHours}h). ${detail}`,
    };
  }
  return null;
}
